<template>
  <PayoutModal
    :open="payout_modal_open"
    :modal_config="payout_modal_config"
    @close-modal="payout_modal_open = false"
  />
  <EarningsBreakdownModal
    :open="isEarningsBreakdownOpen"
    :distribution-date="earningsBreakdownDate"
    :filter="earningsBreakdownFilter"
    @close="isEarningsBreakdownOpen = false"
  />
  <!-- Mobile: bottom CTA card -->
  <div
    class="flex md:hidden rounded-t-2xl justify-center fixed bottom-0 left-0 w-full z-50 bg-gradient-to-b"
    :class="UIStore.is_dark_mode ? 'from-primary-7 to-primary-6' : ' from-paper-5 to-paper-3'"
  >
    <div class="flex md:hidden w-full p-3 gap-2.5 items-center justify-center">
      <Button
        class="grow"
        variant="primary-secondary"
        size="md"
        @click="router.push('/make-an-investment')"
      >
        <template #icon><Icon name="TrendingUp" class="w-5 h-5" /></template>
        <template #label><span class="text-lg font-normal">Invest</span></template>
      </Button>
      <Button class="grow" variant="paper-primary" size="md" @click="downloadStatement()">
        <template #icon>
          <Icon v-if="!download_is_loading" name="Download" class="w-5 h-5" />
          <Icon v-else name="Loader" class="w-5 h-5 animate-spin" />
        </template>
        <template #label><span class="text-lg font-normal">Monthly statement</span></template>
      </Button>
    </div>
  </div>
  <div class="w-full mx-auto flex flex-col gap-5 md:gap-6 mb-24">
    <BreadCrumbs :crumbs="[{ name: 'Portfolio', path: '/portfolio' }]" />
    <Header overtitle="Purchases and Earnings" title="Your Portfolio">
      <template #ctas>
        <div class="flex gap-2">
          <Button
            class="hidden md:flex"
            variant="primary-secondary"
            size="lg"
            @click="router.push('/make-an-investment')"
          >
            <template #icon><Icon name="TrendingUp" class="w-5 h-5" /></template>
            <template #label><span class="text-xl font-normal">Make an investment</span></template>
          </Button>
          <Button
            class="hidden md:flex"
            variant="paper-primary"
            size="lg"
            @click="downloadStatement()"
          >
            <template #icon>
              <Icon v-if="!download_is_loading" name="Download" class="w-5 h-5" />
              <Icon v-else name="Loader" class="w-5 h-5 animate-spin" />
            </template>
            <template #label>
              <span class="text-xl font-normal">Download monthly statement</span>
            </template>
          </Button>
        </div>
      </template>
    </Header>

    <!-- Stat cards -->
    <!-- Desktop: 3-col grid; Next monthly earning spans both rows -->
    <div class="hidden gap-2 md:grid md:grid-cols-3">
      <div class="rounded-xl bg-container-bg p-4">
        <p class="text-sm font-medium text-strong opacity-60">Total active portfolio</p>
        <p class="font-league md:text-3xl font-normal text-strongest">
          {{ totalActivePortfolioValue }}
        </p>
      </div>
      <div class="rounded-xl bg-container-bg p-4">
        <p class="text-sm font-medium text-strong opacity-60">Principal amount</p>
        <p class="font-league text-3xl font-normal text-strongest">
          {{ portfolioSummary.principal }}
        </p>
      </div>
      <div class="row-span-2 flex flex-col gap-2 rounded-xl bg-container-bg p-4">
        <div>
          <p class="text-sm font-medium text-strong opacity-60">Next monthly earning</p>
          <p class="font-league text-3xl font-normal text-success">
            {{ portfolioSummary.nextEarnings }}
          </p>
        </div>
        <div class="mt-auto">
          <button
            type="button"
            class="flex items-center gap-1 text-sm font-medium text-strongest underline-offset-2 hover:underline"
            @click="openEarningsBreakdown()"
          >
            View breakdown
            <Icon name="ExternalLink" class="h-3.5 w-3.5" />
          </button>
          <p class="mt-1 text-sm font-medium text-success">{{ portfolioSummary.nextEarningsOn }}</p>
        </div>
      </div>
      <div class="rounded-xl bg-container-bg p-4">
        <p class="text-sm font-medium text-strong opacity-60">Total accrued interest</p>
        <p class="font-league text-3xl font-normal text-strongest">
          {{ portfolioSummary.accruedInterest }}
        </p>
      </div>
      <div class="rounded-xl bg-container-bg p-4">
        <p class="text-sm font-medium text-strong opacity-60">Total monthly payments</p>
        <p class="font-league text-3xl font-normal text-strongest">
          {{ portfolioSummary.monthlyPayments }}
        </p>
      </div>
    </div>

    <!-- Mobile: plain 2-up stat rows, no card backgrounds -->
    <div class="grid grid-cols-2 gap-x-4 gap-y-1 md:hidden">
      <div>
        <p class="text-md font-medium text-strong opacity-60">Total active portfolio</p>
        <p class="font-league text-2xl font-normal text-strongest">
          {{ totalActivePortfolioValue }}
        </p>
      </div>
      <div>
        <p class="text-md font-medium text-strong opacity-60">Principal amount</p>
        <p class="font-league text-2xl font-normal text-strongest">
          {{ portfolioSummary.principal }}
        </p>
      </div>
      <div>
        <p class="text-md font-medium text-strong opacity-60">Total accrued interest</p>
        <p class="font-league text-2xl font-normal text-strongest">
          {{ portfolioSummary.accruedInterest }}
        </p>
      </div>
      <div>
        <p class="text-md font-medium text-strong opacity-60">Total monthly payments</p>
        <p class="font-league text-2xl font-normal text-strongest">
          {{ portfolioSummary.monthlyPayments }}
        </p>
      </div>
      <div>
        <p class="text-md font-medium text-strong opacity-60">Next monthly earning</p>
        <p class="font-league text-2xl font-normal text-strongest">
          {{ portfolioSummary.nextEarnings }}
        </p>
        <p class="text-md font-medium text-success">{{ portfolioSummary.nextEarningsOn }}</p>
      </div>
      <div class="flex items-center">
        <button
          type="button"
          class="flex items-center gap-1 text-md font-light text-strongest underline-offset-2 hover:underline"
          @click="openEarningsBreakdown()"
        >
          View breakdown
          <Icon name="ExternalLink" class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
    <div>
      <TabView
        vertical
        :tabs="investmentTabs"
        :initialTab="route.query?.selectedTab"
        :jumpToRollovers="route.query?.selectedTab === 'reinvest'"
        @update:activeTab="(tab) => investmentsTabViewed(tab)"
      >
        <!-- EARNINGS -->
        <template #earnings>
          <div
            class="h-full w-full flex flex-col items-center gap-4 bg-container-bg px-3 py-5 md:px-6 md:py-6 rounded-xl"
          >
            <div class="flex flex-row w-full">
              <span class="text-2xl font-semibold text-strongest">Your earnings</span>
            </div>
            <div class="gap-1 w-full flex">
              <RadioPill
                v-for="pill in earningsPills"
                :key="pill.label"
                :label="pill.label"
                v-model="earningsFilter"
                :selected="earningsFilter === pill.value"
                @click="earningsFilter = pill.value"
              />
            </div>

            <!-- Per-account summary cards (desktop) -->
            <div v-if="earningsAccountCards.length" class="hidden w-full grid-cols-2 gap-2 md:grid">
              <EarningsAccountCard
                v-for="card in earningsAccountCards"
                :key="card.key"
                :group="card"
              />
            </div>

            <!-- Per-account summary rows (mobile, tap to expand) -->
            <div v-if="earningsAccountCards.length" class="w-full space-y-1 md:hidden">
              <EarningsAccountCard
                v-for="card in earningsAccountCards"
                :key="card.key"
                :group="card"
                layout="row"
              />
            </div>

            <!-- No account holds this earning type (e.g. every bond compounds
            and the investor selects "Monthly payments"). -->
            <p v-else class="w-full font-inter text-sm font-medium text-strong opacity-60">
              {{ earningsAccountsEmptyText }}
            </p>

            <Table
              :table_headers="earnings_table_headers"
              :table_data="earnings_table_data"
              variant="primary"
              :has_actions="true"
              mobile_primary_field="amount"
              :fields_with_custom_styles="['amount']"
              :hide_headers="false"
              :hide_scrollbar="true"
              class="w-full"
              :paginated="true"
              :pagination_page_size="table_page_size"
              empty_icon="Banknote"
              empty_primary_text="No earnings yet."
              empty_secondary_text="Once your investment has been processed, earnings details will appear here."
              :column_gap_px="48"
              default_sort_field="distribution_date"
              :default_sort_ascending="false"
            >
              <template v-slot:amount="entry">
                <div class="text-success">
                  {{
                    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                      entry.row.amount
                    )
                  }}
                </div>
              </template>
              <template v-slot:actions="entry">
                <div class="flex gap-1">
                  <Button
                    variant="darkpaper-primary"
                    size="sm"
                    @click="openEarningsBreakdown(entry.row)"
                  >
                    <template #label>View breakdown</template>
                  </Button>
                </div>
              </template>
            </Table>
          </div>
        </template>
        <template #investments>
          <div
            class="h-full w-full flex flex-col items-center gap-6.5 justify-between bg-container-bg p-6 rounded-xl min-w-0"
          >
            <div class="flex flex-row w-full min-w-0">
              <span class="text-xl font-semibold text-strongest">Your Investments</span>
            </div>
            <FilterPills
              class="w-full"
              :active-filters="purchaseFilters"
              @apply-filters="addFilter"
              @clear-filter="removeFilter"
              @clear-all-filters="purchaseFilters = []"
              :filters="[
                {
                  filter_type: 'min-max',
                  table: 'investments',
                  label: 'Amount',
                  field_name: 'amount',
                  number_type: 'currency',
                },
                {
                  filter_type: 'picklist',
                  table: 'investments',
                  label: 'Status',
                  field_name: 'status',
                  options: statusFilterOptions,
                },
                {
                  filter_type: 'picklist',
                  table: 'investments',
                  label: 'Type',
                  field_name: 'type',
                  options: [
                    { label: 'Individual', value: 'Individual' },
                    { label: 'Joint', value: 'Joint' },
                    { label: 'Entity', value: 'Entity' },
                    { label: 'IRA / Retirement', value: 'IRA' },
                  ],
                },
                {
                  filter_type: 'picklist',
                  table: 'investments',
                  label: 'Bond Type',
                  field_name: 'bond_type',
                  options: bondTypeFilterOptions,
                },
                {
                  filter_type: 'picklist',
                  table: 'investments',
                  label: 'Payment Frequency',
                  field_name: 'is_compounded',
                  options: [
                    { label: 'Monthly payments', value: false },
                    { label: 'Accrued interest', value: true },
                  ],
                },
                {
                  filter_type: 'date-range',
                  table: 'investments',
                  label: 'Date',
                  field_name: 'date',
                },
                {
                  filter_type: 'min-max',
                  table: 'investments',
                  label: 'Bonds',
                  field_name: 'bonds',
                },
                {
                  filter_type: 'picklist',
                  table: 'investments',
                  label: 'Receiving account',
                  field_name: 'distribution_account',
                  options: receivingAccountOptions,
                },
              ]"
            />
            <div class="w-full">
              <Table
                ref="purchasesTable"
                :table_headers="purchase_table_headers"
                :table_data="purchase_table_data"
                variant="primary"
                :has_actions="true"
                mobile_primary_field="amount"
                :fields_with_custom_styles="[
                  'amount',
                  'record_id',
                  'bond_term',
                  'status',
                  'offering_type',
                ]"
                :hide_headers="false"
                :hide_scrollbar="false"
                :paginated="true"
                :pagination_page_size="table_page_size"
                empty_icon="DollarSign"
                :empty_primary_text="
                  purchaseFilters.length
                    ? 'No Investments match your filters.'
                    : 'No Investments yet.'
                "
                :empty_secondary_text="
                  purchaseFilters.length
                    ? 'Try adjusting your filters to see more results.'
                    : 'Once your investment has been processed, investment details will appear here.'
                "
                :column_gap_px="6"
              >
                <template v-slot:status="entry">
                  <div v-if="entry.row.status === 'Pending S1' && entry.row.pending_s1_funded">
                    {{ `${entry.row.status} (Funded)` }}
                  </div>
                  <div v-else>
                    {{ entry.row.status }}
                  </div>
                </template>
                <template v-slot:amount="entry">
                  <div class="text-success">
                    {{
                      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                        entry.row.amount
                      )
                    }}
                  </div>
                </template>

                <template v-slot:actions="entry">
                  <div class="flex gap-1">
                    <Button
                      variant="darkpaper-primary"
                      size="sm"
                      @click="openInvestmentDetails(entry.row)"
                    >
                      <template #label>Details</template>
                    </Button>
                  </div>
                </template>
                <template v-slot:record_id="entry">
                  <div>***{{ formatID(entry.row.record_id) }}</div>
                </template>
                <template v-slot:bond_term="entry">
                  <div>
                    {{
                      entry.row?.is_short_term
                        ? `${entry.row.bond_term} month term`
                        : formatTerm(entry.row.bond_term)
                    }}
                  </div>
                </template>
                <template v-slot:offering_type="entry">
                  <div>{{ getBondTypeLabel(entry.row) }}</div>
                </template>
              </Table>
            </div>
          </div>
        </template>
        <template #reinvest>
          <div
            class="h-full w-full flex flex-col items-center justify-between bg-container-bg p-6 rounded-xl gap-6.5"
          >
            <div class="flex flex-row w-full">
              <span class="text-xl font-semibold text-strongest"
                >Investments Available to Reinvest</span
              >
            </div>
            <!-- TOOD: Filters -->
            <div class="w-full">
              <!-- Left gradient -->
              <div
                v-if="showScrollLeft"
                class="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
                :style="{
                  background: `linear-gradient(to right, ${gradientBgVar}, transparent)`,
                }"
              ></div>
              <div
                ref="scrollContainer"
                class="gap-1 flex w-full overflow-x-scroll no-scrollbar"
                @scroll="handleScroll"
              >
                <RadioPill
                  v-for="pill in rolloverPills"
                  :key="pill.label"
                  :label="pill.label"
                  v-model="rolloverFilter"
                  :selected="rolloverFilter === pill.value"
                  @click="rolloverFilter = pill.value"
                />
              </div>
              <!-- Right gradient -->

              <div
                v-if="showScrollRight"
                class="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
                :style="{
                  background: `linear-gradient(to left, ${gradientBgVar}, transparent)`,
                }"
              ></div>
            </div>

            <div class="w-full">
              <Table
                :table_headers="rollover_table_headers"
                :table_data="rollover_table_data"
                variant="primary"
                :has_actions="true"
                mobile_primary_field="amount"
                :fields_with_custom_styles="['record_id']"
                :hide_headers="false"
                :hide_scrollbar="false"
                :paginated="true"
                :pagination_page_size="table_page_size"
                empty_icon="RotateCw"
                empty_primary_text="No Investments available to reinvest yet."
                empty_secondary_text="Eligible investments nearing maturity will show up here."
                :column_gap_px="36"
              >
                <!-- <template #time_until_maturity="entry">
                  <div>
                    {{
                      UIStore.daysUntilDate(entry.row.maturity_date)
                        ? UIStore.daysUntilDate(entry.row.maturity_date) + " days"
                        : `${UIStore.daysSinceDate(entry.row.maturity_date)} days ago`
                    }}
                  </div>
                </template> -->
                <template #record_id="entry">
                  <div>***{{ formatID(entry.row.record_id) }}</div>
                </template>
                <template v-slot:actions="entry">
                  <div class="flex gap-1">
                    <Button
                      variant="darkpaper-primary"
                      size="sm"
                      @click="
                        router.push(`/make-an-investment?rolloverInvestment=${entry.row.record_id}`)
                      "
                    >
                      <template #label>Reinvest</template>
                    </Button>
                  </div>
                </template>
              </Table>
            </div>
          </div>
        </template>
        <template #holding_maturities>
          <div
            class="h-full w-full flex flex-col items-center gap-6.5 justify-between bg-container-bg p-6 rounded-xl min-w-0"
          >
            <div class="flex flex-row w-full min-w-0">
              <span class="text-xl font-semibold text-strongest">Holding Maturities</span>
            </div>
            <div class="text-sm text-info italic font-medium flex">
              <Icon name="Asterisk" class="mr-1 min-h-4 min-w-4 h-4 w-4 -skew-x-12" />
              <div>
                <div>
                  A <span class="font-bold">Holding Maturity</span> is a bond that has reached its
                  maturity date and is no longer earning interest. The investment remains in this
                  status when no instructions have been received regarding reinvestment of the
                  matured funds or return of funds.
                </div>
                <div class="mt-2">
                  To explore options for reinvesting your funds, please contact our team as soon as
                  possible at 855.732.6395.
                </div>
              </div>
            </div>
            <div class="w-full">
              <Table
                :table_headers="[
                  { label: 'Funds Returned Status', field_name: 'user_requested_payout' },
                  ...purchase_table_headers,
                ]"
                :table_data="holding_maturities_data"
                variant="primary"
                :has_actions="true"
                mobile_primary_field="amount"
                :fields_with_custom_styles="[
                  'amount',
                  'record_id',
                  'bond_term',
                  'status',
                  'user_requested_payout',
                ]"
                :hide_headers="false"
                :hide_scrollbar="false"
                :paginated="true"
                :pagination_page_size="table_page_size"
                empty_icon="Clock"
                empty_primary_text="No holding maturities."
                empty_secondary_text="Investments with matured bonds pending payout will appear here."
                :column_gap_px="6"
              >
                <template v-slot:user_requested_payout="entry">
                  <div
                    class="pb-1 pt-2 px-3 rounded-full text-center w-fit flex items-center justify-center leading-none font-medium"
                    :class="
                      entry.row.user_requested_payout
                        ? 'bg-success text-white'
                        : 'bg-container-bg text-strongest'
                    "
                  >
                    {{ entry.row.user_requested_payout ? "Requested" : "Holding" }}
                  </div>
                </template>
                <template v-slot:status="entry">
                  <div v-if="entry.row.status === 'Pending S1' && entry.row.pending_s1_funded">
                    {{ `${entry.row.status} (Funded)` }}
                  </div>
                  <div v-else>
                    {{ entry.row.status }}
                  </div>
                </template>
                <template v-slot:amount="entry">
                  <div class="text-success">
                    {{
                      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                        entry.row.amount
                      )
                    }}
                  </div>
                </template>
                <template v-slot:actions="entry">
                  <div class="flex gap-1">
                    <Button
                      v-if="entry.row.user_requested_payout"
                      variant="darkpaper-primary"
                      size="xs"
                      icon
                      label
                      @click="requestPayout(entry.row, 'cancel_payout_request')"
                    >
                      <template #icon>
                        <Icon name="X" class="w-4 h-4 text-outline-error" />
                      </template>

                      <template #label>Cancel Request</template>
                    </Button>
                    <Button
                      v-if="!entry.row.user_requested_payout"
                      variant="darkpaper-primary"
                      size="xs"
                      @click="requestPayout(entry.row, 'request_payout')"
                    >
                      <template #label>Return Funds</template>
                    </Button>
                  </div>
                </template>
                <template v-slot:record_id="entry">
                  <div
                    class="cursor-pointer text-cta-primary hover:underline text-secondary-7"
                    @click="openInvestmentDetails(entry.row)"
                  >
                    ***{{ formatID(entry.row.record_id) }}
                  </div>
                </template>
                <template v-slot:bond_term="entry">
                  <div>{{ formatTerm(entry.row.bond_term) }}</div>
                </template>
              </Table>
            </div>
          </div>
        </template>
      </TabView>
    </div>
  </div>
</template>

<script setup>
import {
  Button,
  Icon,
  TabView,
  Header,
  Table,
  BreadCrumbs,
  FilterPills,
  RadioPill,
  EarningsAccountCard,
  EarningsBreakdownModal,
} from "@/components";
import { useRoute, useRouter } from "vue-router";
import {
  useDataStore,
  useUIStore,
  useInvestmentStore,
  useCrudStore,
  useSchemaStore,
} from "@/stores";
import {
  formatMonthYear,
  formatID,
  commaSeparateThousands,
  formatTerm,
  getLastFour,
} from "@/utilities";
import { portfolioDataSource } from "@/constants/samplePortfolioData";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import useFileUtility from "@/composables/useFileUtility";
import { useGradientScroll } from "@/composables/useGradientScroll";
import { useAnalytics } from "@/composables/useAnalytics";
import _ from "lodash";
import PayoutModal from "./PayoutModal.vue";

const { downloadFile_R2 } = useFileUtility();
const {
  investmentsTabViewed,
  investmentsFilterApplied,
  investmentDetailsOpened,
  statementDownloaded,
} = useAnalytics();
const download_is_loading = ref(false);

// script for investments
const router = useRouter();
const route = useRoute();
const dataStore = useDataStore();
const UIStore = useUIStore();
const investmentStore = useInvestmentStore();
const schemaStore = useSchemaStore();
const crudStore = useCrudStore();
const { scrollContainer, showScrollLeft, showScrollRight, handleScroll } = useGradientScroll();
const payout_modal_open = ref(false);
const payout_modal_config = ref({
  action: "",
  investment_id: null,
});

let metrics = ref([]);
const purchasesTable = ref(null);

// ── Portfolio stat cards + per-account summary cards (PHX-151) ──
// Shares the same store getters as the dashboard hero so the numbers match.
const portfolioSummary = computed(() => investmentStore.getPortfolioSummary());
const totalActivePortfolioValue = computed(() => investmentStore.getTotalActivePortfolioValue());

// Per-account cards on the Earnings tab. These sit under the payment-type pills
// and follow them: "Monthly payments" narrows every card to the bonds that pay
// out in cash, "Accrued interest" to the bonds that compound. The stat cards at
// the top of the page are portfolio-wide and deliberately stay unfiltered.
const earningsAccountCards = computed(() =>
  investmentStore.getPortfolioAccountCards(earningsFilter.value)
);

const earningsAccountsEmptyText = computed(() => {
  if (earningsFilter.value === "simple") {
    return "None of your investments are set to pay interest out monthly.";
  }
  if (earningsFilter.value === "compounded") {
    return "None of your investments are set to compound their interest.";
  }
  return "Your investments will be summarized here once they settle.";
});

// Every table on this page reads its records through here rather than off the
// data store directly, so the design-testing switch in samplePortfolioData.js
// swaps the whole page at once instead of leaving half of it empty.
const portfolioData = computed(() => portfolioDataSource(dataStore));

// Receiving-account filter options (Investments tab) — from the investor's linked banks.
// Investments store the chosen bank as `distribution_account` (a bank record_id).
const receivingAccountOptions = computed(() =>
  portfolioData.value.bank_accounts.map((bank) => ({
    label: `${bank.financial_institution_name} ****${getLastFour(bank.account_number)}`,
    value: bank.record_id,
  }))
);

// Entry point for the earnings-breakdown modal/sheet (WS3). Called with a row
// of the earnings history to break down that payment, or with nothing from the
// "Next monthly earning" card to break down the next scheduled one.
const isEarningsBreakdownOpen = ref(false);
const earningsBreakdownDate = ref(null);
const earningsBreakdownFilter = ref("all");
function openEarningsBreakdown(row = null) {
  earningsBreakdownDate.value = row?.distribution_date ?? null;
  // A row's totals only cover the earning type the pills are filtered to, so
  // the breakdown behind it has to be filtered the same way to add up.
  earningsBreakdownFilter.value = row ? earningsFilter.value : "all";
  isEarningsBreakdownOpen.value = true;
}

onMounted(async () => {
  await getEarningsData();
  setBreadCrumbs();
});

onUnmounted(() => {
  // remove banking query param
  const { selectedTab, ...newQuery } = route.query; // remove selectedTab from query params
  router.replace({ query: newQuery }); // replace current route with new query params, without selectedTab
});

const holding_maturities_data = computed(() => {
  return portfolioData.value.investments.filter(
    (inv) => inv.status === "Bonds Matured" && inv.paid_out !== true
  );
});

const investmentTabs = computed(() => {
  const default_tabs = [
    { label: "Earnings", icon: "Banknote", slot_name: "earnings" },
    { label: "Investments", icon: "DollarSign", slot_name: "investments" },
    {
      label: "Rollovers available",
      icon: "RotateCw",
      slot_name: "reinvest",
    },
  ];
  let tabs = [...default_tabs];
  if (holding_maturities_data.value.length > 0) {
    tabs.push({ label: "Holding Maturities", icon: "Clock", slot_name: "holding_maturities" });
  }
  return tabs;
});

let purchase_table_headers = [
  { label: "Investment ID", field_name: "record_id", is_object_id: true },
  { label: "Transaction Date", field_name: "signature_date", field_type: "date" },
  { label: "Type", field_name: "type" },
  { label: "Status", field_name: "status" },
  { label: "Amount", field_name: "amount", field_type: "number", number_type: "currency" },
  { label: "Bonds", field_name: "bonds", field_type: "number" },
  { label: "Offering Type", field_name: "offering_type" },
  {
    label: "Interest Rate",
    field_name: "interest_rate",
    field_type: "number",
    number_type: "percentage",
  },
  // term
  { label: "Term", field_name: "bond_term", field_type: "number" },
  { label: "Is Compounded?", field_name: "is_compounded", field_type: "boolean" },
  { label: "Maturity Date", field_name: "maturity_date", field_type: "date" },
  {
    label: "Total Earned",
    field_name: "inception_to_date_interest_paid",
    field_type: "number",
    number_type: "currency",
  },
  {
    label: "YTD Earned",
    field_name: "year_to_date_interest_paid",
    field_type: "number",
    number_type: "currency",
  },
];

let rollover_table_headers = [
  { label: "Investment ID", field_name: "record_id" },
  { label: "Offering Type", field_name: "offering_type" },
  {
    label: "Available to Reinvest",
    field_name: "rollover_available",
    field_type: "number",
    number_type: "currency",
  },
  { label: "Time Until Maturity", field_name: "time_until_maturity" },
];

let earnings_table_headers = [
  { label: "Date", field_name: "distribution_date", field_type: "date" },
  { label: "Total Amount", field_name: "amount", field_type: "number", number_type: "currency" },
  {
    label: "Monthly Payment",
    field_name: "simple_amount",
    field_type: "number",
    number_type: "currency",
  },
  {
    label: "Accrued Interest",
    field_name: "compounding_amount",
    field_type: "number",
    number_type: "currency",
  },
];

// Groups raw investment `type` values into the Type filter's buckets. Source
// data is inconsistent in case ("individual" vs "Individual") and has many
// distinct retirement-account strings (SEP IRA, HSA, Solo 401(k), etc.) that
// should all filter under one "IRA / Retirement" pill.
function getInvestmentTypeCategory(type) {
  const normalized = (type || "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === "individual") return "Individual";
  if (normalized === "joint") return "Joint";
  if (normalized === "entity") return "Entity";
  if (normalized.includes("ira") || normalized === "hsa" || normalized.includes("401(k)")) {
    return "IRA";
  }
  return null;
}

// Buckets an investment into the general bond type investors recognize (PHX-363).
// Phoenix Flex Notes are stored as ordinary S1 records carrying `is_short_term`,
// so they have to be split off before falling back to the raw offering_type.
function getBondTypeCategory(investment) {
  if (!investment) return null;
  if (investment.is_short_term) return "Flex";
  return (investment.offering_type || "").trim() || null;
}

const BOND_TYPE_FILTER_OPTIONS = [
  { label: "Reg D", value: "Reg D" },
  { label: "Registered Offering (S1)", value: "S1" },
  { label: "Phoenix Flex", value: "Flex" },
  { label: "Reg A", value: "Reg A" },
  { label: "Exchange Note", value: "Exchange Note" },
];

// Label shown in the Offering Type column, so a row reads back the same bucket
// name the Bond Type pill used to select it.
function getBondTypeLabel(investment) {
  const category = getBondTypeCategory(investment);
  if (!category) return "";
  return BOND_TYPE_FILTER_OPTIONS.find((option) => option.value === category)?.label ?? category;
}

// Investments eligible to appear in the table at all, before any user-picked
// filters are applied. Shared by the table itself and by statusFilterOptions
// below, so the Status pills only ever offer a status the investor actually has.
const visibleInvestments = computed(() => {
  return portfolioData.value.investments.filter((entry) => {
    // Must have a signature_date OR be in Pending/Pending S1 status
    const hasSignatureDate = !!entry.signature_date;
    const isPending = ["Pending", "Pending S1"].includes(entry.status);
    const isCancelled = entry.status === "Canceled";
    if (!hasSignatureDate && !isPending) return false;
    if (isCancelled) return false;
    return true;
  });
});

const STATUS_FILTER_OPTIONS = [
  { label: "Settled", value: "Settled" },
  { label: "Matured", value: "Bonds Matured" },
  { label: "Pending", value: "Pending" },
  { label: "Pending S1", value: "Pending S1" },
  { label: "Created", value: "Created" },
  { label: "Funded", value: "Funded" },
  { label: "Early Redemption", value: "Early Redemption" },
  { label: "Title Transferred", value: "Title Transferred" },
];

// Scoped to visibleInvestments (not the currently-filtered result set) so
// picking a Type/Amount/etc. filter can't make a Status pill disappear mid-session.
const statusFilterOptions = computed(() => {
  const presentStatuses = new Set(visibleInvestments.value.map((inv) => inv.status));
  return STATUS_FILTER_OPTIONS.filter((option) => presentStatuses.has(option.value));
});

const bondTypeFilterOptions = computed(() => {
  const presentTypes = new Set(visibleInvestments.value.map(getBondTypeCategory));
  return BOND_TYPE_FILTER_OPTIONS.filter((option) => presentTypes.has(option.value));
});

const purchase_table_data = computed(() => {
  // group filters by field_name
  const filtersByField = purchaseFilters.value.reduce((acc, filter) => {
    acc[filter.field_name] ??= [];
    acc[filter.field_name].push(filter);
    return acc;
  }, {});

  const filtered_data = visibleInvestments.value.filter((entry) => {
    return Object.entries(filtersByField).every(([field, filters]) => {
      // AND across fields, OR within the same field
      return filters.some((filter) => {
        // numeric range
        if (filter.filter_type === "min-max") {
          const { min, max } = filter;

          const value = entry[field];
          if (value == null) return false;

          if (min != null && value < min) return false;
          if (max != null && value > max) return false;

          return true;
        }

        // date range
        if (filter.filter_type === "date-range") {
          const { start, end } = filter;

          const entryDate = new Date(entry[field]);
          if (isNaN(entryDate)) return false;

          if (start) {
            const startDate = new Date(start);
            if (entryDate < startDate) return false;
          }

          if (end) {
            const endDate = new Date(end);
            if (entryDate > endDate) return false;
          }

          return true;
        }

        // Type filter buckets several raw type strings under one category
        if (field === "type") {
          return getInvestmentTypeCategory(entry.type) === filter.value;
        }

        // Bond Type is derived from offering_type + is_short_term, not a column
        if (field === "bond_type") {
          return getBondTypeCategory(entry) === filter.value;
        }

        // exact match (booleans, enums, strings, etc)
        return entry[field] === filter.value;
      });
    });
  });

  // set pagination back to 1 whenever filters change
  if (purchasesTable.value) purchasesTable.value.goToPage(1);

  // sorting options
  const sortField = "signature_date";
  return filtered_data.slice().sort((a, b) => {
    const aHasDate = !!a.signature_date;
    const bHasDate = !!b.signature_date;

    // Pending items without a signature_date float to the top
    if (!aHasDate && bHasDate) return -1;
    if (aHasDate && !bHasDate) return 1;

    // Both lack a date — keep original order
    if (!aHasDate && !bHasDate) return 0;

    const valA = a[sortField];
    const valB = b[sortField];

    // Date comparison
    if (sortField === "signature_date" && valA && valB) {
      return new Date(valB) - new Date(valA);
    }
    // For numbers
    if (typeof valA === "number" && typeof valB === "number") {
      return valB - valA;
    }
    // For strings
    if (typeof valA === "string" && typeof valB === "string") {
      return valB.localeCompare(valA);
    }
    return 0;
  });
});

const purchaseFilters = ref([]);

// Full earnings history: unlike the dashboard's "Your returns" summary this
// keeps earnings from bonds that have since matured or been redeemed, so the
// investor's record of what they were paid stays complete.
const earnings_table_data = computed(() =>
  investmentStore.getEarningsRows(earningsFilter.value, { includeClosedInvestments: true })
);

const rollover_table_data = computed(() => {
  const allInvestments = portfolioData.value.investments;

  // Build pending deductions by parent
  const pendingByParent = {};
  for (const inv of allInvestments) {
    if (inv.status === "Pending" && !inv.signature_date && inv.parent_investment) {
      pendingByParent[inv.parent_investment] =
        (pendingByParent[inv.parent_investment] || 0) + (inv.amount || 0);
    }
  }

  let data = _.cloneDeep(
    allInvestments.filter((inv) => {
      if (!inv.able_to_rollover) return false;
      if (inv.status != "Settled") return false;
      const adjusted = (inv.rollover_available || 0) - (pendingByParent[inv.record_id] || 0);
      return adjusted >= 1000;
    })
  );

  // Apply adjusted rollover_available to each entry
  data.forEach((entry) => {
    entry.rollover_available =
      (entry.rollover_available || 0) - (pendingByParent[entry.record_id] || 0);
  });

  if (rolloverFilter.value === "reg-d") {
    data = data.filter((inv) => inv.offering_type === "Reg D");
  }
  if (rolloverFilter.value === "s1") {
    data = data.filter((inv) => inv.offering_type === "S1");
  }
  if (rolloverFilter.value === "reg-a") {
    data = data.filter((inv) => inv.offering_type === "Reg A");
  }

  data.forEach((entry) => {
    const maturityDate = new Date(entry.maturity_date);
    const now = new Date();
    if (isNaN(maturityDate.getTime())) {
      entry.time_until_maturity = "-";
    } else {
      const timeDiff = maturityDate - now;
      if (timeDiff > 0) {
        entry.time_until_maturity = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + " days";
      } else {
        entry.time_until_maturity = "Matured";
      }
    }
  });

  return data;
});

// calc sum of distributions for next month's 10th
const current_earnings = computed(() => {
  let total = 0;
  let now = new Date();
  let nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 10);
  dataStore?.distributions?.forEach((distribution) => {
    let distributionDate = new Date(distribution.distribution_date);
    if (
      distributionDate.getFullYear() === nextMonth.getFullYear() &&
      distributionDate.getMonth() === nextMonth.getMonth()
    ) {
      total += distribution.amount;
    }
  });
  return total;
});

const earningsPills = ref([
  { label: "All", value: "all" },
  { label: "Monthly payments", value: "simple" },
  { label: "Accrued interest", value: "compounded" },
]);

const earningsFilter = ref("all");

const rolloverPills = ref([
  { label: "All", value: "all" },
  { label: "Registered Offering", value: "s1" },
  { label: "Regulation D", value: "reg-d" },
  { label: "Regulation A", value: "reg-a" },
]);

const rolloverFilter = ref("all");

const table_page_size = ref(6);

async function requestPayout(investment, action) {
  payout_modal_config.value = {
    action,
    investment_id: investment.record_id,
  };
  payout_modal_open.value = true;

  // let value = action === "request" ? true : false;

  // const query = [
  //   {
  //     field_name: "record_id",
  //     operator: "eq",
  //     value: investment.record_id,
  //   },
  // ];
  // let update = { user_requested_payout: value };

  // update = schemaStore.addCreatedUpdatedFields(update, false, true);

  // console.log("Requesting payout with query:", query, "and update:", update);
  // const res = await crudStore.update("investments", query, update);
  // if (res.status === 200) {
  //   investment.user_requested_payout = value;
  // }
}

watch(earningsFilter, async () => {
  await getEarningsData();
});

function addFilter(filteringEvent) {
  let { field_name, filters } = filteringEvent;

  // Remove any filters for this field
  purchaseFilters.value = purchaseFilters.value.filter((f) => f.field_name !== field_name);

  // Add the new filter(s)
  purchaseFilters.value.push(...filters);
  if (filters.length) investmentsFilterApplied(filters[0].filter_type, field_name);
}

function openInvestmentDetails(row) {
  investmentDetailsOpened({
    investment_id: row.record_id ?? row.investment_id,
    investment_type: row.offering_type ?? null,
    status: row.status ?? null,
    amount: row.amount ?? null,
    type_tag: row.type ?? null,
  });
  router.push(`/investment-details?recordId=${row.record_id ?? row.investment_id}`);
}

function removeFilter(filter) {
  purchaseFilters.value = purchaseFilters.value.filter(
    (f) => !(f.field_name === filter.field_name && f.value === filter.value)
  );
}

async function getEarningsData() {
  //if (dataStore.is_app_initialized) {
  setTimeout(() => {
    UIStore.is_chart_loading = true;
    UIStore.sort_ascending = true;

    let chartData = investmentStore.getDashboardChartData(null, earningsFilter.value);
    if (!investmentStore.hasNoDistributions) {
      const first = chartData[0];

      if (first) {
        const prevMonth = first.month === 1 ? 12 : first.month - 1;
        const prevYear = first.month === 1 ? first.year - 1 : first.year;

        chartData.unshift({
          year: prevYear,
          month: prevMonth,
          compoundedAmount: 0,
          runningNonCompoundedAmount: 0,
        });
      } else {
        chartData.unshift({
          year: first.year,
          month: first.month - 1,
          compoundedAmount: 0,
          runningNonCompoundedAmount: 0,
        });
      }
    }
    investmentStore.chartData.labels = chartData.map((entry) =>
      formatMonthYear(entry.year, entry.month)
    );
    investmentStore.chartData.values = chartData.map(
      (entry) => entry.compoundedAmount + entry.runningNonCompoundedAmount
    );
    metrics.value = investmentStore.calculateDashboardInvestorMetrics(null, earningsFilter.value);
    UIStore.is_chart_loading = false;
  }, 100);
  //}
}

function setBreadCrumbs() {
  let new_crumb = {
    name: "Portfolio",
    path: `/portfolio`,
  };
  UIStore.breadCrumbs = [new_crumb];
}

async function downloadStatement() {
  let status = "";

  if (!dataStore?.statement) {
    await UIStore.animateNotificationAlert({
      type: "error",
      message: "No statement available for download.",
      subText: "",
      is_read: false,
      does_persist: false,
      action: null,
      icon: "X",
    });
    status = "No statement available";
  } else {
    download_is_loading.value = true;
    await downloadFile_R2(dataStore.statement);
    statementDownloaded();
    download_is_loading.value = false;
    status = "Statement downloaded";
  }

  let log = {
    step: "Download Statement",
    substep: "",
    status: status,
    message: `Downloaded statement`,
    url: dataStore?.statement?.r2_key || "",
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  await crudStore.insert("investoractivity", [log]);
}
</script>
